import React, {
    useState,
} from 'react';
import {
    Grid,
    Divider,
    Box,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import HappyBox from "assets/images/Comercial/GenerateOrder/HappyBox.png"

interface PropsInterface {
    item: any,
    productSelected: (data: any) => void,
    productCopy?: any
    showCheckbox: boolean
    showDetails: boolean
    showPrice: boolean
    image?: string
}

const ItemOrder = (props: PropsInterface) => {
    const {
        item
    } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [openImageModal, setOpenImageModal] = useState(false);
    const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

    const handleImageMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        const timer = setTimeout(() => {
            setOpenImageModal(true);
        }, 500);
        setLongPressTimer(timer);
    };

    const handleImageMouseUp = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    const handleImageTouchStart = (e: React.TouchEvent) => {
        e.stopPropagation();
        const timer = setTimeout(() => {
            setOpenImageModal(true);
        }, 500);
        setLongPressTimer(timer);
    };

    const handleImageTouchEnd = (e: React.TouchEvent) => {
        e.stopPropagation();
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    const imageUrl = item?.image || HappyBox;

    // Vista Mobile
    if (isMobile) {
        return (
            <>
            <Card
                sx={{
                    marginTop: "16px",
                    marginBottom: "12px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                        boxShadow: "0 4px 16px rgba(48, 181, 149, 0.2)",
                        transform: "translateY(-2px)",
                    },
                }}
                onClick={() => props.productSelected(item)}
            >
                <CardContent sx={{ padding: "12px", "&:last-child": { paddingBottom: "12px" } }}>
                    {/* Imagen y Nombre */}
                    <Grid container spacing={1.5} sx={{ marginBottom: "12px", alignItems: "flex-start" }}>
                        <Grid item xs="auto">
                            <Box
                                sx={{
                                    width: "50px",
                                    height: "50px",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor: "grab",
                                    "&:active": {
                                        cursor: "grabbing",
                                    }
                                }}
                                onMouseDown={handleImageMouseDown}
                                onMouseUp={handleImageMouseUp}
                                onTouchStart={handleImageTouchStart}
                                onTouchEnd={handleImageTouchEnd}
                            >
                                <img
                                    src={imageUrl}
                                    alt="Producto"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        userSelect: "none",
                                    }}
                                    draggable={false}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs>
                            <Box sx={{ paddingTop: "4px" }}>
                                <p style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    margin: "0 0 4px 0",
                                    color: "#173456",
                                }}>
                                    {item?.ItemName}
                                </p>
                                <p style={{
                                    fontSize: "12px",
                                    margin: "0",
                                    color: "#999",
                                }}>
                                    SKU: {item?.id}
                                </p>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Detalles de Inventario */}
                    {props.showDetails && item?.onHandDetails && (
                        <Box sx={{ 
                            marginBottom: "8px", 
                            padding: "10px", 
                            backgroundColor: "#f0fdf9",
                            borderRadius: "6px",
                            border: "1px solid #30B595",
                        }}>
                            <p style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                margin: "0 0 8px 0",
                                color: "#30B595",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}>
                                Inventario
                            </p>
                            {item?.onHandDetails?.map((inventory: any, idx: number) => (
                                <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between">
                                    <p style={{
                                        fontSize: "11px",
                                        margin: "3px 0",
                                        color: "#333",
                                        fontWeight: "500",
                                    }}>
                                        • {inventory.split(':')[0]}
                                    </p>
                                    <p style={{
                                        fontSize: "11px",
                                        margin: "3px 0",
                                        color: "#666",
                                        fontWeight: "400",
                                    }}>
                                        {inventory.split(':')[1]}
                                    </p>
                                </Stack>
                            ))}
                        </Box>
                    )}

                    {/* Precio */}
                    {props.showPrice && (
                        <Box sx={{ paddingTop: "8px", borderTop: "2px solid #30B595" }}>
                            <p style={{
                                fontSize: "13px",
                                color: "#999",
                                margin: "4px 0",
                            }}>
                                Precio
                            </p>
                            <p style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                color: "#30B595",
                                margin: "0",
                            }}>
                                ${parseFloat(item?.price?.toString()).toFixed(2)}
                            </p>
                        </Box>
                    )}
                </CardContent>
            </Card>
            <Dialog
                open={openImageModal}
                onClose={() => setOpenImageModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        backgroundImage: "none",
                    }
                }}
            >
                <DialogContent
                    sx={{
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        backgroundColor: "#000",
                        minHeight: "400px",
                    }}
                >
                    <IconButton
                        onClick={() => setOpenImageModal(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#fff",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                            },
                            zIndex: 10,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={imageUrl}
                        alt="Producto ampliado"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            objectFit: "contain",
                            padding: "20px",
                        }}
                    />
                </DialogContent>
            </Dialog>
            </>
        );
    }

    // Vista Tablet
    if (isTablet) {
        return (
            <>
            <Box
                sx={{
                    marginTop: "16px",
                    padding: "16px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                        boxShadow: "0 4px 12px rgba(48, 181, 149, 0.15)",
                        backgroundColor: "#f9fafb",
                    },
                }}
                onClick={() => props.productSelected(item)}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs="auto">
                        <Box
                            sx={{
                                width: "70px",
                                height: "70px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                backgroundColor: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "grab",
                                "&:active": {
                                    cursor: "grabbing",
                                }
                            }}
                            onMouseDown={handleImageMouseDown}
                            onMouseUp={handleImageMouseUp}
                            onTouchStart={handleImageTouchStart}
                            onTouchEnd={handleImageTouchEnd}
                        >
                            <img
                                src={imageUrl}
                                alt="Producto"
                                style={{
                                    width: "80%",
                                    height: "80%",
                                    objectFit: "contain",
                                    userSelect: "none",
                                }}
                                draggable={false}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs>
                        <p style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            margin: "0 0 4px 0",
                            color: "#173456",
                        }}>
                            {item?.ItemName}
                        </p>
                        <p style={{
                            fontSize: "13px",
                            margin: "0 0 8px 0",
                            color: "#999",
                        }}>
                            {item?.id}
                        </p>
                        {props.showDetails && item?.onHandDetails && (
                            <Box sx={{ 
                                marginTop: "8px",
                                padding: "10px",
                                backgroundColor: "#f0fdf9",
                                borderRadius: "6px",
                                border: "1px solid #30B595",
                            }}>
                                <p style={{
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    margin: "0 0 6px 0",
                                    color: "#30B595",
                                    textTransform: "uppercase",
                                }}>
                                    Inventario
                                </p>
                                {item?.onHandDetails?.map((inventory: any, idx: number) => (
                                    <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between">
                                        <p style={{
                                            fontSize: "11px",
                                            margin: "3px 0",
                                            color: "#333",
                                            fontWeight: "500",
                                        }}>
                                            • {inventory.split(':')[0]}
                                        </p>
                                        <p style={{
                                            fontSize: "11px",
                                            margin: "3px 0",
                                            color: "#666",
                                            fontWeight: "400",
                                        }}>
                                            {inventory.split(':')[1]}
                                        </p>
                                    </Stack>
                                ))}
                            </Box>
                        )}
                    </Grid>

                    {props.showPrice && (
                        <Grid item xs="auto" sx={{ textAlign: "right" }}>
                            <p style={{
                                fontSize: "13px",
                                color: "#999",
                                margin: "0 0 4px 0",
                            }}>
                                Precio
                            </p>
                            <p style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#30B595",
                                margin: "0",
                            }}>
                                ${parseFloat(item?.price?.toString()).toFixed(2)}
                            </p>
                        </Grid>
                    )}
                </Grid>
                <Divider variant="middle" sx={{ height: "1px", backgroundColor: "#30B595", marginTop: "12px", opacity: 0.3 }} />
            </Box>
            <Dialog
                open={openImageModal}
                onClose={() => setOpenImageModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        backgroundImage: "none",
                    }
                }}
            >
                <DialogContent
                    sx={{
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        backgroundColor: "#000",
                        minHeight: "400px",
                    }}
                >
                    <IconButton
                        onClick={() => setOpenImageModal(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#fff",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                            },
                            zIndex: 10,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={imageUrl}
                        alt="Producto ampliado"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            objectFit: "contain",
                            padding: "20px",
                        }}
                    />
                </DialogContent>
            </Dialog>
            </>
        );
    }

    // Vista Web
    return (
        <>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}
            sx={{
                marginTop: "20px",
            }}
        >
            <Box
                sx={{
                    padding: "16px",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                        boxShadow: "0 4px 12px rgba(48, 181, 149, 0.2)",
                        backgroundColor: "#f9fafb",
                        transform: "translateY(-4px)",
                    }
                }}
                onClick={() => props.productSelected(item)}
            >
                {/* Imagen */}
                <Box
                    sx={{
                        width: "100%",
                        height: "120px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "12px",
                        cursor: "grab",
                        "&:active": {
                            cursor: "grabbing",
                        }
                    }}
                    onMouseDown={handleImageMouseDown}
                    onMouseUp={handleImageMouseUp}
                    onTouchStart={handleImageTouchStart}
                    onTouchEnd={handleImageTouchEnd}
                >
                    <img
                        src={imageUrl}
                        alt="Producto"
                        style={{
                            width: "85%",
                            height: "85%",
                            objectFit: "contain",
                            userSelect: "none",
                        }}
                        draggable={false}
                    />
                </Box>

                {/* Contenido */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <p style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: "0 0 4px 0",
                        color: "#173456",
                        lineHeight: "1.3",
                    }}>
                        {item?.ItemName}
                    </p>
                    <p style={{
                        fontSize: "12px",
                        margin: "0 0 12px 0",
                        color: "#999",
                    }}>
                        ID: {item?.id}
                    </p>

                    {/* Inventario */}
                    {props.showDetails && item?.onHandDetails && (
                        <Box sx={{ 
                            marginBottom: "12px", 
                            flex: 1,
                            padding: "10px",
                            backgroundColor: "#f0fdf9",
                            borderRadius: "6px",
                            border: "1px solid #30B595",
                        }}>
                            <p style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                margin: "0 0 8px 0",
                                color: "#30B595",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}>
                                Inventario
                            </p>
                                {item?.onHandDetails?.map((inventory: any, idx: number) => (
                                <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between">
                                    <p style={{
                                        fontSize: "11px",
                                        margin: "3px 0",
                                        color: "#333",
                                        fontWeight: "500",
                                    }}>
                                        • {inventory.split(':')[0]}
                                        </p>
                                    <p style={{
                                        fontSize: "11px",
                                            margin: "3px 0",
                                            color: "#666",
                                            fontWeight: "400",
                                        }}>
                                        {inventory.split(':')[1]}
                                    </p>
                                </Stack>
                            ))}
                        </Box>
                    )}

                    {/* Precio */}
                    {props.showPrice && (
                        <Box sx={{ paddingTop: "12px", borderTop: "1px solid #eee" }}>
                            <p style={{
                                fontSize: "11px",
                                color: "#999",
                                margin: "8px 0 4px 0",
                            }}>
                                PRECIO
                            </p>
                            <p style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                color: "#30B595",
                                margin: "0",
                            }}>
                                ${parseFloat(item?.price?.toString()).toFixed(2)}
                            </p>
                        </Box>
                    )}
                </Box>
            </Box>
        </Grid>
        <Dialog
            open={openImageModal}
            onClose={() => setOpenImageModal(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    backgroundImage: "none",
                }
            }}
        >
            <DialogContent
                sx={{
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    backgroundColor: "#ffffff",
                    minHeight: "400px",
                }}
            >
                <IconButton
                    onClick={() => setOpenImageModal(false)}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "#fff",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                        zIndex: 10,
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <img
                    src={imageUrl}
                    alt="Producto ampliado"
                    style={{
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                        padding: "20px",
                    }}
                />
            </DialogContent>
        </Dialog>
        </>
    );
}

export default ItemOrder;